import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Section } from '@/components/manager/overview/Section'
import { EndTrialNowDialog } from './EndTrialNowDialog'
import type { Subscription } from '@/api/subscription'

interface Props {
  subscription: Subscription
  darkMode: boolean
}

/**
 * Carte affichée sur la page Facturation pendant la période d'essai.
 * Propose à l'user de basculer en mode payant immédiatement (même plan,
 * sans limites). Action séparée de "Changer de plan" (upgrade Pro).
 */
export function ActivateNowCard({ subscription, darkMode }: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <Section
      title={t('manager.billing.activateNow.cardTitle', 'Activer dès aujourd\'hui')}
      icon={<Zap className="h-5 w-5" />}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg',
              darkMode ? 'bg-blue-900/30' : 'bg-blue-50',
            )}
          >
            <Sparkles
              className={cn(
                'w-5 h-5',
                darkMode ? 'text-blue-400' : 'text-blue-600',
              )}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className={cn(
                'text-sm font-medium',
                darkMode ? 'text-white' : 'text-slate-900',
              )}
            >
              {t(
                'manager.billing.activateNow.cardHeadline',
                'Retirez les limites de votre essai',
              )}
            </p>
            <p
              className={cn(
                'text-sm mt-0.5',
                darkMode ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              {t(
                'manager.billing.activateNow.cardDescription',
                'Activez votre plan {{plan}} aujourd\'hui pour bénéficier de l\'accès illimité immédiatement.',
                { plan: subscription.plan_display ?? 'Starter' },
              )}
            </p>
          </div>
        </div>

        <Button
          onClick={() => setOpen(true)}
          className="flex-shrink-0 bg-[#0A192F] hover:bg-[#0A192F]/90 text-white dark:bg-white dark:text-[#0A192F] dark:hover:bg-white/90"
        >
          <Zap className="h-4 w-4 mr-2" />
          {t('manager.billing.activateNow.cardCta', 'Activer maintenant')}
        </Button>
      </div>

      <EndTrialNowDialog open={open} onOpenChange={setOpen} />
    </Section>
  )
}
